import * as React from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Appearance,
  Dimensions,
  Keyboard,
  Modal,
  Platform,
  ViewStyle,
} from "react-native"
import { observer } from "mobx-react-lite"
import { Actionsheet as BaseActionsheet, NativeBaseProvider, View } from "native-base"
import { IActionsheetProps } from "native-base/lib/typescript/components/composites/Actionsheet/types"
import { nativeBaseTheme } from "../../theme"
import { Text } from "../text/text"
import { TxKeyPath } from "../../i18n"
import { Button } from "../button/button"
import { useSafeAreaInsets } from "react-native-safe-area-context"

export interface ActionsheetProps extends IActionsheetProps {
  actionsheetStyle?: ViewStyle,

  titleTx?: TxKeyPath,
  title?: string,

  clearable?: boolean,
  onClear?: () => any;

  colorScheme?: "light" | "dark",
}

const TITLE_CONTAINER: ViewStyle = {
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  paddingHorizontal: 16,
  marginBottom: 8
}

const actionSheetIosAwareHOC = (WrappedComponent) => (Platform.OS === 'ios') ? ((props) =>  (
  <Modal transparent visible={props.isOpen}>
    <NativeBaseProvider theme={nativeBaseTheme}>
      <WrappedComponent {...props} />
    </NativeBaseProvider>
  </Modal>
)) : WrappedComponent

const ActionsheetIosAware = actionSheetIosAwareHOC(BaseActionsheet)

/**
 * Describe your component here
 */
export const Actionsheet = observer(function Actionsheet(props: ActionsheetProps) {
  const {
    isOpen, onClose: onCloseProp, onClear: onClearProp, actionsheetStyle,
    title, titleTx, clearable = false,
    colorScheme = Appearance.getColorScheme(),
    children,
    ...rest
  } = props
  const insets = useSafeAreaInsets();
  const [keyboardHeight, setKeyboardHeight] = useState<number>(0)
  const [keyboardDidShow, setKeyboardDidShow] = useState<boolean>(false)

  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener(Platform.OS === 'ios' ? "keyboardWillShow" : "keyboardDidShow", (event) => {
      setKeyboardHeight(event.endCoordinates.height)
      setKeyboardDidShow(true)
    });
    const keyboardDidHideListener = Keyboard.addListener(Platform.OS === 'ios' ? "keyboardWillHide" : "keyboardDidHide", (event) => {
      setKeyboardHeight(0)
      setKeyboardDidShow(false)
    });

    return () => {
      keyboardDidShowListener.remove()
      keyboardDidHideListener.remove()
    }
  }, [])

  const actionSheetContentBaseStyle = useMemo(() => nativeBaseTheme.components.ActionsheetContent.baseStyle({ colorMode: colorScheme }), [colorScheme])

  const onClose = useCallback(() => {
    onCloseProp && onCloseProp()
  }, [onCloseProp])

  const onClear = useCallback(() => {
    onClearProp && onClearProp()
    onClose()
  }, [onClearProp, onClose])

  const contentStyles = [
    { minHeight: Dimensions.get("window").height * 0.3 },
    actionsheetStyle,
    keyboardDidShow ? {
      maxHeight: Dimensions.get("window").height * 0.5,
      get minHeight() {
        const height = actionsheetStyle?.minHeight == null ? Dimensions.get("window").height * 0.3 : actionsheetStyle.minHeight;
        if (height > this.maxHeight) return this.maxHeight;
        return height;
      },
      marginBottom: (Platform.OS === "ios" ? keyboardHeight : 0) - insets.bottom
    } : {},
  ]

  return (
    <ActionsheetIosAware
      isOpen={isOpen}
      onClose={onClose}
      {...rest}
    >
      <BaseActionsheet.Content {...actionSheetContentBaseStyle} backgroundColor={nativeBaseTheme.colors[colorScheme]["50"]} alignItems="stretch" px={0} py={0} style={contentStyles}>
        {(titleTx || title) && <View style={TITLE_CONTAINER}>
          <Text tx={titleTx} text={title} preset="title3" colorScheme={colorScheme} />
          <View flexDirection="row">
            {clearable && (<>
              <Button
                testID="jala:id/clear-button"
                tx="Clear"
                preset="link"
                textPreset="title4"
                colorScheme={colorScheme}
                textColor={nativeBaseTheme.colors.danger[colorScheme === "light" ? "700" : "300"]}
                onPress={onClear}
              />
              <View marginRight="12px" />
            </>)}
            <Button
              testID="jala:id/close-button"
              tx="Close"
              preset="link"
              textPreset="title4"
              colorScheme={colorScheme}
              onPress={onClose}
            />
          </View>
        </View>}
        {children}
      </BaseActionsheet.Content>
    </ActionsheetIosAware>
  )
})
